import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import ServerError from '../error/ServerError';
import Loading from '../../components/shared/Loading';

import {
  Avatar,
  Box,
  Card,
  CardHeader,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { GET_TRAINING_PLAN_SESSIONS } from '../../graphql/training_plan_session';

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'STOPPED', label: 'Stopped' },
];

const formatDate = (date) => {
  if (!date) {
    return 'Not set';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
};

const getSessionStatusColor = (status) => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'COMPLETED':
      return 'primary';
    case 'STOPPED':
      return 'default';
    default:
      return 'default';
  }
};

export default function TrainingPlanAnalytics({ trainingPlan }) {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const input = useMemo(() => {
    const queryInput = { trainingPlanId: trainingPlan?._id, limit: 50 };

    if (statusFilter !== 'ALL') {
      queryInput.status = statusFilter;
    }

    return queryInput;
  }, [statusFilter, trainingPlan?._id]);

  const { loading, error, data } = useQuery(GET_TRAINING_PLAN_SESSIONS, {
    variables: { input },
    skip: !trainingPlan?._id,
  });

  if (!trainingPlan) {
    return <ServerError message="Failed to load" />;
  }
  if (error) return <ServerError message={error.message || 'Failed to load'} />;
  if (loading || !data?.trainingPlanSessions) return <Loading />;
  const trainingPlanSessions = data?.trainingPlanSessions;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Typography variant="h3">Enrolled Users</Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="training-plan-session-status-filter-label">
            Status
          </InputLabel>
          <Select
            labelId="training-plan-session-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box>
        {trainingPlanSessions.length === 0 && (
          <Typography variant="caption" gutterBottom>
            No enrolled users found for this status.
          </Typography>
        )}
        {trainingPlanSessions.map((trainingPlanSession) => {
          const user = trainingPlanSession.user;
          const completedEventCount =
            trainingPlanSession.completedEvents?.length ?? 0;
          const userUrl = user?._id ? `/trainer/users/${user._id}` : undefined;
          const userTitle = userUrl ? (
            <Typography
              color="textPrimary"
              component={RouterLink}
              to={userUrl}
              variant="h6"
              sx={{ textDecoration: 'none' }}
            >
              {user?.name || 'Unknown user'}
            </Typography>
          ) : (
            <Typography color="textPrimary" component="span" variant="h6">
              {user?.name || 'Unknown user'}
            </Typography>
          );

          return (
            <Card key={trainingPlanSession._id} style={{ marginBottom: 16 }}>
              <CardHeader
                avatar={
                  userUrl ? (
                    <Avatar
                      alt="Person"
                      component={RouterLink}
                      src={user?.avatarUrl}
                      to={userUrl}
                    />
                  ) : (
                    <Avatar alt="Person" src={user?.avatarUrl} />
                  )
                }
                subheader={
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" color="textSecondary">
                      Enrolled {formatDate(trainingPlanSession.enrolledAt)}
                      {trainingPlanSession.completedAt
                        ? ` · Completed ${formatDate(
                            trainingPlanSession.completedAt
                          )}`
                        : ''}
                    </Typography>
                  </Box>
                }
                title={
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {userTitle}
                    <Chip
                      size="small"
                      label={trainingPlanSession.status || 'UNKNOWN'}
                      color={getSessionStatusColor(trainingPlanSession.status)}
                    />
                  </Box>
                }
              />
              <CardContent>
                <Typography component="div" variant="body1" color="textPrimary">
                  Progress: {completedEventCount}
                  {' '}completed events
                  <br />
                  Trainer ID:{' '}
                  {trainingPlanSession.trainingPlan?.trainerId ||
                    trainingPlanSession.trainerId ||
                    'Unknown'}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
